import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, Loader2, User, Building, Mail, Phone, MapPin,
    Calendar, CreditCard, FileText, AlertCircle, Check, Globe,
    Briefcase, UserCheck
} from 'lucide-react';
import { clientService } from '@services/clientService';
import CustomSelect from '../shared/CustomSelect';

const ClientFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [activeTab, setActiveTab] = useState('basic');
    const [errors, setErrors] = useState({});
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchingClient, setFetchingClient] = useState(false);

    const [formData, setFormData] = useState({
        clientType: 'RETAIL',
        // Retail fields
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        gender: '',
        nationalId: '',
        kraPin: '',
        passportNumber: '',
        email: '',
        email2: '',
        phoneNumber: '',
        phoneNumber2: '',
        address: '',
        city: '',
        country: '',
        // Corporate fields
        companyName: '',
        registrationNumber: '',
        taxIdNumber: '',
        industry: '',
        contactPersonName: '',
        contactPersonEmail: '',
        contactPersonPhone: '',
        // Common fields
        crossSellingConsent: false,
        active: true
    });

    // Load client data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            fetchClientData();
        }
    }, [id, isEditMode]);

    const fetchClientData = async () => {
        try {
            setFetchingClient(true);
            console.log('Fetching client with ID:', id);
            const response = await clientService.getById(id);
            console.log('Client fetch response:', response);

            if (response.success === false) {
                throw new Error(response.message || 'Failed to fetch client');
            }

            const client = response.data || response;
            console.log('Client data:', client);

            // Populate form with client data
            setFormData({
                clientType: client.clientType || 'RETAIL',
                // Retail fields
                firstName: client.firstName || '',
                middleName: client.middleName || '',
                lastName: client.lastName || '',
                dateOfBirth: client.dateOfBirth || '',
                gender: client.gender || '',
                nationalId: client.nationalId || '',
                kraPin: client.kraPin || '',
                passportNumber: client.passportNumber || '',
                email: client.email || '',
                email2: client.email2 || '',
                phoneNumber: client.phoneNumber || '',
                phoneNumber2: client.phoneNumber2 || '',
                address: client.address || '',
                city: client.city || '',
                country: client.country || '',
                // Corporate fields
                companyName: client.companyName || client.name || '',
                registrationNumber: client.registrationNumber || '',
                taxIdNumber: client.taxIdNumber || '',
                industry: client.industry || '',
                contactPersonName: client.contactPersonName || '',
                contactPersonEmail: client.contactPersonEmail || '',
                contactPersonPhone: client.contactPersonPhone || '',
                // Common fields
                crossSellingConsent: client.crossSellingConsent || false,
                active: client.active !== false
            });
        } catch (error) {
            console.error('Error fetching client:', error);
            setErrorMessage(`Failed to load client data: ${error.message}`);
            setShowErrorMessage(true);
            setTimeout(() => {
                navigate('/clients');
            }, 3000);
        } finally {
            setFetchingClient(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Primary email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Primary phone number is required';
        }

        if (formData.clientType === 'RETAIL') {
            if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
            if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

            if (formData.email2 && !/\S+@\S+\.\S+/.test(formData.email2)) {
                newErrors.email2 = 'Invalid secondary email format';
            }
        } else {
            if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';

            if (formData.contactPersonEmail && !/\S+@\S+\.\S+/.test(formData.contactPersonEmail)) {
                newErrors.contactPersonEmail = 'Invalid contact person email format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submit button clicked');

        if (validateForm()) {
            console.log('Form validation passed');
            setLoading(true);
            setShowErrorMessage(false);
            setShowSuccessMessage(false);

            try {
                // Prepare data matching backend DTOs
                const payload = formData.clientType === 'RETAIL' ? {
                    firstName: formData.firstName,
                    middleName: formData.middleName || '',
                    lastName: formData.lastName,
                    dateOfBirth: formData.dateOfBirth || null,
                    gender: formData.gender || null,
                    nationalId: formData.nationalId || '',
                    kraPin: formData.kraPin || '',
                    passportNumber: formData.passportNumber || '',
                    email: formData.email,
                    email2: formData.email2 || '',
                    phoneNumber: formData.phoneNumber,
                    phoneNumber2: formData.phoneNumber2 || '',
                    address: formData.address || '',
                    city: formData.city || '',
                    country: formData.country || '',
                    crossSellingConsent: formData.crossSellingConsent
                } : {
                    companyName: formData.companyName,
                    registrationNumber: formData.registrationNumber || '',
                    taxIdNumber: formData.taxIdNumber || '',
                    industry: formData.industry || '',
                    contactPersonName: formData.contactPersonName || '',
                    contactPersonEmail: formData.contactPersonEmail || '',
                    contactPersonPhone: formData.contactPersonPhone || '',
                    email: formData.email,
                    phoneNumber: formData.phoneNumber,
                    address: formData.address || '',
                    city: formData.city || '',
                    country: formData.country || '',
                    crossSellingConsent: formData.crossSellingConsent
                };

                console.log('Sending payload:', payload);
                console.log('Client type:', formData.clientType);
                console.log('Edit mode:', isEditMode);

                // Call the appropriate API endpoint based on mode and client type
                let response;
                if (isEditMode) {
                    // Update existing client
                    if (formData.clientType === 'RETAIL') {
                        console.log('Calling updateRetail...');
                        response = await clientService.updateRetail(id, payload);
                    } else {
                        console.log('Calling updateCorporate...');
                        response = await clientService.updateCorporate(id, payload);
                    }
                } else {
                    // Create new client
                    if (formData.clientType === 'RETAIL') {
                        console.log('Calling createRetail...');
                        response = await clientService.createRetail(payload);
                    } else {
                        console.log('Calling createCorporate...');
                        response = await clientService.createCorporate(payload);
                    }
                }

                console.log('API Response:', response);

                setLoading(false);
                setShowSuccessMessage(true);

                // Navigate after success
                setTimeout(() => {
                    setShowSuccessMessage(false);
                    if (isEditMode) {
                        navigate(`/clients/${id}`);
                    } else {
                        if (response?.data?.id) {
                            navigate(`/clients/${response.data.id}`);
                        } else if (response?.id) {
                            navigate(`/clients/${response.id}`);
                        } else {
                            navigate('/clients');
                        }
                    }
                }, 2000);

            } catch (error) {
                setLoading(false);
                console.error('Full error object:', error);

                let message = 'Failed to save client. Please try again.';
                if (error.response) {
                    message = error.response.data?.message ||
                        error.response.data?.error ||
                        `Server error: ${error.response.status}`;
                } else if (error.message) {
                    message = error.message;
                }

                setErrorMessage(message);
                setShowErrorMessage(true);

                setTimeout(() => {
                    setShowErrorMessage(false);
                }, 5000);
            }
        } else {
            console.log('Form validation failed', errors);
            const errorFields = Object.keys(errors);
            if (errorFields.some(field => ['firstName', 'lastName', 'companyName', 'gender'].includes(field))) {
                setActiveTab('basic');
            } else if (errorFields.some(field => ['email', 'phoneNumber'].includes(field))) {
                setActiveTab('contact');
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const calculateCompletion = () => {
        const requiredFields = formData.clientType === 'RETAIL'
            ? ['firstName', 'lastName', 'email', 'phoneNumber']
            : ['companyName', 'email', 'phoneNumber'];

        const optionalFields = formData.clientType === 'RETAIL'
            ? ['middleName', 'nationalId', 'gender', 'dateOfBirth', 'email2', 'phoneNumber2', 'address', 'city', 'country']
            : ['registrationNumber', 'taxIdNumber', 'industry', 'contactPersonName', 'contactPersonEmail', 'contactPersonPhone', 'address', 'city', 'country'];

        const requiredFilled = requiredFields.filter(field => formData[field] && formData[field].toString().trim()).length;
        const optionalFilled = optionalFields.filter(field => formData[field] && formData[field].toString().trim()).length;

        const totalFields = requiredFields.length + optionalFields.length;
        const filledFields = requiredFilled + optionalFilled;

        return Math.round((filledFields / totalFields) * 100);
    };

    const completionPercentage = calculateCompletion();

    // Show loading while fetching client data
    if (fetchingClient) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading client data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(isEditMode ? `/clients/${id}` : '/clients')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {isEditMode ? 'Edit Client' : 'Create New Client'}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {isEditMode ? 'Update client information' : 'Client code will be auto-generated'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <p className="text-sm text-gray-500">Profile Completion</p>
                            <div className="flex items-center gap-2 mt-1">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all ${
                                            completionPercentage === 100 ? 'bg-green-600' :
                                                completionPercentage >= 75 ? 'bg-blue-600' :
                                                    completionPercentage >= 50 ? 'bg-yellow-600' : 'bg-orange-600'
                                        }`}
                                        style={{ width: `${completionPercentage}%` }}
                                    />
                                </div>
                                <span className="text-sm font-medium text-gray-700">{completionPercentage}%</span>
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? 'Update Client' : 'Save Client'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Success/Error Messages */}
            {showSuccessMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">
                        Client {isEditMode ? 'updated' : 'created'} successfully!
                    </p>
                </div>
            )}

            {showErrorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
            )}

            {/* Client Type Selection - Only show in create mode */}
            {!isEditMode && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Type</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            type="button"
                            onClick={() => handleInputChange('clientType', 'RETAIL')}
                            className={`flex items-center justify-center gap-3 p-4 border-2 rounded-lg transition ${
                                formData.clientType === 'RETAIL'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <User className="w-6 h-6" />
                            <div className="text-left">
                                <p className="font-medium">Retail Client</p>
                                <p className="text-sm opacity-75">Individual person</p>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleInputChange('clientType', 'CORPORATE')}
                            className={`flex items-center justify-center gap-3 p-4 border-2 rounded-lg transition ${
                                formData.clientType === 'CORPORATE'
                                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                                    : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <Building className="w-6 h-6" />
                            <div className="text-left">
                                <p className="font-medium">Corporate Client</p>
                                <p className="text-sm opacity-75">Business entity</p>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Tabs and Form Fields */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="border-b">
                    <nav className="flex -mb-px">
                        <button
                            type="button"
                            onClick={() => setActiveTab('basic')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'basic'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Basic Information
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('contact')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'contact'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Contact Details
                        </button>
                        {formData.clientType === 'CORPORATE' && (
                            <button
                                type="button"
                                onClick={() => setActiveTab('corporate')}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                    activeTab === 'corporate'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                Corporate Details
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setActiveTab('settings')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                activeTab === 'settings'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Settings
                        </button>
                    </nav>
                </div>

                <div className="p-6">
                    {/* Basic Information Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            {formData.clientType === 'RETAIL' ? (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <User className="w-4 h-4 inline mr-1" />
                                                First Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.firstName}
                                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter first name"
                                            />
                                            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Middle Name
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.middleName}
                                                onChange={(e) => handleInputChange('middleName', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter middle name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.lastName}
                                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                                placeholder="Enter last name"
                                            />
                                            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Calendar className="w-4 h-4 inline mr-1" />
                                                Date of Birth
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.dateOfBirth}
                                                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer relative"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Gender
                                            </label>
                                            <CustomSelect
                                                value={formData.gender}
                                                onChange={(val) => handleInputChange('gender', val)}
                                                options={[
                                                    { value: '', label: 'Select gender' },
                                                    { value: 'MALE', label: 'Male' },
                                                    { value: 'FEMALE', label: 'Female' },
                                                    { value: 'OTHER', label: 'Other' }
                                                ]}
                                                placeholder="Select gender"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <CreditCard className="w-4 h-4 inline mr-1" />
                                                National ID
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.nationalId}
                                                onChange={(e) => handleInputChange('nationalId', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter national ID"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <FileText className="w-4 h-4 inline mr-1" />
                                                KRA PIN
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.kraPin}
                                                onChange={(e) => handleInputChange('kraPin', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="e.g. A012345678Z"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Globe className="w-4 h-4 inline mr-1" />
                                                Passport Number
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.passportNumber}
                                                onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter passport number"
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Building className="w-4 h-4 inline mr-1" />
                                            Company Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.companyName}
                                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.companyName ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="Enter company name"
                                        />
                                        {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Briefcase className="w-4 h-4 inline mr-1" />
                                            Industry
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.industry}
                                            onChange={(e) => handleInputChange('industry', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g. Technology, Finance, Healthcare"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contact Details Tab */}
                    {activeTab === 'contact' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Mail className="w-4 h-4 inline mr-1" />
                                        Primary Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="example@email.com"
                                    />
                                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Mail className="w-4 h-4 inline mr-1" />
                                        Secondary Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email2}
                                        onChange={(e) => handleInputChange('email2', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.email2 ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="alternative@email.com"
                                    />
                                    {errors.email2 && <p className="mt-1 text-sm text-red-600">{errors.email2}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Phone className="w-4 h-4 inline mr-1" />
                                        Primary Phone *
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneNumber}
                                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="+254 700 000 000"
                                    />
                                    {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Phone className="w-4 h-4 inline mr-1" />
                                        Secondary Phone
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phoneNumber2}
                                        onChange={(e) => handleInputChange('phoneNumber2', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="+254 700 000 000"
                                    />
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <MapPin className="w-4 h-4 inline mr-1" />
                                        Address
                                    </label>
                                    <textarea
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        rows="3"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter full address"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Globe className="w-4 h-4 inline mr-1" />
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.city}
                                            onChange={(e) => handleInputChange('city', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g. Nairobi"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Globe className="w-4 h-4 inline mr-1" />
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.country}
                                            onChange={(e) => handleInputChange('country', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g. Kenya"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Corporate Details Tab (Only for Corporate) */}
                    {activeTab === 'corporate' && formData.clientType === 'CORPORATE' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <FileText className="w-4 h-4 inline mr-1" />
                                        Registration Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.registrationNumber}
                                        onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Company registration number"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <CreditCard className="w-4 h-4 inline mr-1" />
                                        Tax ID Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.taxIdNumber}
                                        onChange={(e) => handleInputChange('taxIdNumber', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Tax identification number"
                                    />
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="text-sm font-semibold text-gray-700 mb-4">Contact Person Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <UserCheck className="w-4 h-4 inline mr-1" />
                                            Contact Person Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.contactPersonName}
                                            onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Full name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Mail className="w-4 h-4 inline mr-1" />
                                            Contact Person Email
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.contactPersonEmail}
                                            onChange={(e) => handleInputChange('contactPersonEmail', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                errors.contactPersonEmail ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                            placeholder="contact@company.com"
                                        />
                                        {errors.contactPersonEmail && <p className="mt-1 text-sm text-red-600">{errors.contactPersonEmail}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <Phone className="w-4 h-4 inline mr-1" />
                                            Contact Person Phone
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.contactPersonPhone}
                                            onChange={(e) => handleInputChange('contactPersonPhone', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="+254 700 000 000"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && (
                        <div className="space-y-6">
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Preferences</h3>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <Mail className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">Cross-Selling Consent</p>
                                                <p className="text-sm text-gray-500">Allow us to send promotional offers and product updates</p>
                                            </div>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.crossSellingConsent}
                                                onChange={(e) => handleInputChange('crossSellingConsent', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>

                                    {isEditMode && (
                                        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-green-100 rounded-lg">
                                                    <UserCheck className="w-5 h-5 text-green-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">Account Status</p>
                                                    <p className="text-sm text-gray-500">Enable or disable this client account</p>
                                                </div>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.active}
                                                    onChange={(e) => handleInputChange('active', e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">Data Privacy Notice</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            All client information is stored securely and handled in accordance with data protection regulations.
                                            {formData.crossSellingConsent && " Marketing communications can be opted out at any time."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientFormPage;
